import {
  Outlet,
  Route,
  createRoutesFromElements,
  redirect,
  type LoaderFunction,
  type LoaderFunctionArgs,
} from 'react-router-dom';
import {
  AdminOverviewPage,
  AdminProductLandingPage,
  DataNotFoundPage,
  Drilldown,
  ProductLandingPage,
  ReportLayout,
  OverviewPage,
  EngagementFeedPage,
  LexilePage,
  GEPage,
  SkillPage,
  DataErrorBoundary,
  isSupportedProduct,
  getReportsForProduct,
  isAdminSupportedProduct,
  getAdminReportsForProduct,
  validateDateRangeParams,
  applicationsQuery as fulfillmentApplicationsQuery,
  applicationsHierarchyQuery,
  orgHierarchyQuery,
  teachersByOrgQuery,
  classesByEdAppQuery,
  studentsByClassAndEdAppQuery,
  type ReportPath,
} from 'eds-data-ux';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { toast } from '@scholastic/mockingjay';
import { ApiError, MAGAZINE_GROUP_CODE } from 'eds-common';
import {
  meQueryOptions,
  meResponseShape,
  getProductByAccessCodeApi,
  getProductByAccessCodeApiQueryKey,
  getProductByProductCodeApi,
  getPermissionsApi,
  getPermissionsApiQueryKey,
  hasExtendedPermissions,
  showsAdminExperience,
  ADMINISTRATOR_ROLE,
} from 'eds-common/api/v2';
import { AppLayout } from '@/pages/AppLayout/AppLayout';
import { RootLayout } from '@/pages/RootLayout/RootLayout';
import { rosterUserQuery } from '@/hooks/api/use-roster-user/use-roster-user';
import { SignOutPage } from '@/pages/SignOutPage/SignOutPage';
import { ErrorBoundary } from '@/components/ErrorBoundary/ErrorBoundary';
import { ByRole } from '@/components/ByRole/ByRole';
import { getFlag } from '@/services/launchdarkly/launchdarkly-client';
import { AdminDataSection } from '@/components/AdminDataSection/AdminDataSection';
import { signOutAction } from '@/services/app-session/sign-out-action';
import { clientEnvsQuery } from './hooks/api/use-client-envs/use-client-envs';
import { PAGE_TYPE, SITE_SECTION } from '@/constants';
import { AppProvider } from '@/contexts/AppContext/AppContext';
import { applicationsQuery } from '@/hooks/api/use-application/use-application';
import { entitlementsQuery } from '@/hooks/api/use-entitlements/use-entitlements';
import { hasOnlyKlAccess } from '@/utils/application-utils';
import { customJwtQuery } from '@/hooks/api/use-custom-jwt/use-custom-jwt';
import { getApplicationLaunchRoute } from '@/util/getApplicationLaunchRoute';
import { RedirectError } from '@/errors/RedirectError';
import type { DeepLinkNoAccessState } from '@/components/AccessCodeRedemptionDialog/DeepLinkNoAccessDialog';
import {
  ACCESS_CODE_QUERY_PARAM,
  ACCESS_CODE_STORAGE_KEY,
  APP_QUERY_PARAM,
  APP_STATE_STORAGE_KEY,
  APP_STORAGE_KEY,
  REDEEM_STORAGE_KEY,
  STATE_QUERY_PARAM,
  clearAccessCodeDeepLink,
  consumeStoredAppDeepLink,
  hasApplicationAccess,
  isEntitledToProductCode,
  isRedeemDeepLinkRequested,
} from '@/components/AccessCodeRedemptionDialog/access-code-deep-link';

export const queryClient = new QueryClient();
const storyPageTitle = 'Story Page';

// Helper function to check access for student work routes
async function checkAccessForStudentWorkRoutes(request: Request) {
  if (request.url.includes('/work')) {
    try {
      const applications = await queryClient.ensureQueryData(
        applicationsQuery()
      );
      // Block if user has ONLY 'KL' access
      if (hasOnlyKlAccess(applications)) {
        // Redirect back to base URL
        const cleanUrl = request.url.split('/work')[0];
        if (cleanUrl) {
          return redirect(cleanUrl);
        }
      }
    } catch (error) {
      const cleanUrl = request.url.split('/work')[0];
      if (cleanUrl) {
        return redirect(cleanUrl);
      }
    }
  }
  return null;
}

const ROLE_REDIRECT_PATHS = {
  teacher: '/',
  administrator: '/',
  student: '/student',
  classPassword: '/student',
  site_auth: '/siteauth',
  site_auth_admin: '/siteauth',
} as const;

function composeLoaders(...loaders: LoaderFunction[]) {
  return async (args: LoaderFunctionArgs) => {
    const results = await Promise.all(loaders.map((loader) => loader(args)));
    return results.reduce((acc, result) => ({ ...acc, ...result }), {});
  };
}

function encodeAppState(state: string | null) {
  return state ? encodeURIComponent(state) : undefined;
}

function isUnauthenticated(error: unknown) {
  return (
    error instanceof ApiError &&
    (error.cause.status === 401 || error.cause.status === 403)
  );
}

async function authLoaderWithSessionContext() {
  const user = await queryClient.ensureQueryData(meQueryOptions());
  return { user };
}

async function authLoader() {
  const meResponse = await queryClient.ensureQueryData(meQueryOptions());
  return { user: meResponse.userContext };
}

function roleGuardLoader(allowedRoles: (keyof typeof ROLE_REDIRECT_PATHS)[]) {
  return async () => {
    const { user } = await authLoader();
    const { currentRole } = user;
    if (!(currentRole in ROLE_REDIRECT_PATHS)) {
      return redirect('/sign-in');
    }
    const role = currentRole as keyof typeof ROLE_REDIRECT_PATHS;
    if (!allowedRoles.includes(role)) {
      return redirect(ROLE_REDIRECT_PATHS[role]);
    }
    return { user };
  };
}

function launchStoredDeepLinkIfPresent() {
  const storedApp = sessionStorage.getItem(APP_STORAGE_KEY);
  const storedAppState = sessionStorage.getItem(APP_STATE_STORAGE_KEY);
  if (storedApp) {
    throw new RedirectError(
      getApplicationLaunchRoute(storedApp, false, storedAppState ?? undefined)
    );
  }
}

// Resolves a stored `?app=` deep link: launch it when the user has access,
// otherwise signal the no-access dialog (never launch an un-entitled app).
// Students get the same launch/no-access resolution as staff, but always the
// `general` no-access variant — they can't redeem access codes, so the
// magazine "add product" path never applies to them.
async function resolveAppDeepLink({ student = false } = {}) {
  const storedApp = sessionStorage.getItem(APP_STORAGE_KEY);
  if (!storedApp) {
    return {
      accessCodeRedemptionInitialState: null,
      appDeepLinkNoAccess: null,
      pendingAppLaunch: null,
    };
  }

  // A user with zero apps 404s here
  const applications = await queryClient
    .ensureQueryData(applicationsQuery())
    .catch(() => []);

  if (hasApplicationAccess(applications, storedApp)) {
    launchStoredDeepLinkIfPresent();
    return {
      accessCodeRedemptionInitialState: null,
      appDeepLinkNoAccess: null,
      pendingAppLaunch: null,
    };
  }

  // No access
  const pendingAppLaunch = consumeStoredAppDeepLink();

  // Students can't redeem, so skip the magazine lookup and always show the
  // general no-access dialog.
  if (student) {
    return {
      accessCodeRedemptionInitialState: null,
      appDeepLinkNoAccess: { variant: 'general' } as DeepLinkNoAccessState,
      pendingAppLaunch: null,
    };
  }

  const product = await getProductByProductCodeApi(storedApp).catch(() => null);
  const appDeepLinkNoAccess: DeepLinkNoAccessState =
    product && product.groupCode === MAGAZINE_GROUP_CODE
      ? { variant: 'magazine', productName: product.productName }
      : { variant: 'general' };

  return {
    accessCodeRedemptionInitialState: null,
    appDeepLinkNoAccess,
    pendingAppLaunch,
  };
}

async function homeDeepLinkLoader() {
  const user = (await queryClient.ensureQueryData(meQueryOptions()))
    .userContext;

  // Only resolve the deep link for roles whose home route is `/`. Other roles
  // are redirected away by the parent guard and resolve it on their own home
  // route; this loader runs in parallel with that redirect, so bail before
  // touching sessionStorage.
  const role = user.currentRole as keyof typeof ROLE_REDIRECT_PATHS;
  if (ROLE_REDIRECT_PATHS[role] !== '/') {
    return {
      accessCodeRedemptionInitialState: null,
      appDeepLinkNoAccess: null,
      pendingAppLaunch: null,
    };
  }

  const redeemRequested = Boolean(sessionStorage.getItem(REDEEM_STORAGE_KEY));

  const accessCode = sessionStorage.getItem(ACCESS_CODE_STORAGE_KEY);
  if (accessCode) {
    clearAccessCodeDeepLink();

    const entitlements = await queryClient.ensureQueryData(
      entitlementsQuery(user.userId)
    );

    const product = await queryClient
      .fetchQuery({
        queryKey: getProductByAccessCodeApiQueryKey(accessCode),
        queryFn: () => getProductByAccessCodeApi(accessCode),
      })
      .catch(() => {
        toast.error('The access code is expired or does not exist.', {
          position: 'top-center',
        });
        return null;
      });

    if (!product) {
      return {
        accessCodeRedemptionInitialState: null,
        appDeepLinkNoAccess: null,
        openAccessCodeRedemption: redeemRequested,
        pendingAppLaunch: null,
      };
    }

    if (!isEntitledToProductCode(entitlements, product.productCode)) {
      return {
        accessCodeRedemptionInitialState: { accessCode, product },
        appDeepLinkNoAccess: null,
        pendingAppLaunch: consumeStoredAppDeepLink(),
      };
    }

    return resolveAppDeepLink();
  }

  if (redeemRequested) {
    clearAccessCodeDeepLink();
    return {
      accessCodeRedemptionInitialState: null,
      appDeepLinkNoAccess: null,
      openAccessCodeRedemption: true,
      pendingAppLaunch: consumeStoredAppDeepLink(),
    };
  }

  return resolveAppDeepLink();
}

async function studentHomeDeepLinkLoader() {
  sessionStorage.removeItem(REDEEM_STORAGE_KEY);
  return resolveAppDeepLink({ student: true });
}

/** The loader half of `useAdminExperience`; both apply `showsAdminExperience`. */
async function usesAdminExperience() {
  const { user } = await authLoader();

  const isAdministrator = user.currentRole === ADMINISTRATOR_ROLE;

  // Administrators are settled by role, so neither request can change the
  // answer. For everyone else the two run together: the flag read waits on the
  // LaunchDarkly identify, which costs nothing while permissions are in flight.
  const [hasExtendedPermission, showAdminReportFlag] = isAdministrator
    ? [false, false]
    : await Promise.all([
        queryClient
          .ensureQueryData({
            queryKey: getPermissionsApiQueryKey(),
            queryFn: () => getPermissionsApi(),
          })
          .then(hasExtendedPermissions, () => false),
        getFlag(user, 'dndShowAdminReport', false),
      ]);

  return showsAdminExperience({
    isAdministrator,
    hasExtendedPermission,
    showAdminReportFlag,
  });
}

/** An admin's products come from the hierarchy, not their own entitlements. */
async function applicationsForRequest() {
  const query = (await usesAdminExperience())
    ? applicationsHierarchyQuery()
    : fulfillmentApplicationsQuery();

  return queryClient.ensureQueryData(query);
}

/** Whether a product is on the dashboard, per the config for this request. */
async function isSupportedProductForRequest(productCode: string) {
  return (await usesAdminExperience())
    ? isAdminSupportedProduct(productCode)
    : isSupportedProduct(productCode);
}

/** The report paths a product offers, per the config for this request. */
async function allowedReportPathsForRequest(
  productId: string,
  userProductCodes: string[]
): Promise<string[]> {
  if (await usesAdminExperience()) {
    return getAdminReportsForProduct(productId, userProductCodes).map(
      (report) => report.path
    );
  }

  const { user } = await authLoader();
  const reports = getReportsForProduct(productId, userProductCodes, {
    dndShowSkillsReport: await getFlag(user, 'dndShowSkillsReport', false),
  });

  return reports.map((report) => report.path);
}

async function validateOrgParams({ request }: LoaderFunctionArgs) {
  const orgIds = new URL(request.url).searchParams.getAll('orgId');
  if (orgIds.length === 0) return;

  if (!(await usesAdminExperience())) return;

  // The whole hierarchy: a selection is carried across products, and the
  // report drops what this one does not hold rather than 404ing.
  const orgs = await queryClient.ensureQueryData(orgHierarchyQuery());
  const orgIdSet = new Set(orgs.map((org) => org.id));

  if (!orgIds.every((orgId) => orgIdSet.has(orgId))) {
    // eslint-disable-next-line @typescript-eslint/no-throw-literal
    throw new Response('Not Found', { status: 404 });
  }
}

// Checks if the report is configured for the current product.
// Also applies product-code-level exclusions (e.g. R4RWB-only users cannot access ge-score).
function reportGuardLoader(reportPath: ReportPath) {
  return async ({ params }: LoaderFunctionArgs) => {
    const productId = params.productId!;
    const applications = await applicationsForRequest();
    const userProductCodes =
      applications
        .find((app) => app.applicationCode === productId)
        ?.products.map((p) => p.productCode) ?? [];

    const allowed = await allowedReportPathsForRequest(
      productId,
      userProductCodes
    );
    if (!allowed.includes(reportPath)) {
      // eslint-disable-next-line @typescript-eslint/no-throw-literal
      throw new Response('Not Found', { status: 404 });
    }
    return null;
  };
}

/** The product codes the signed-in user holds for one application. */
async function productCodesFor(productId: string) {
  const applications = await applicationsForRequest();
  return (
    applications
      .find((app) => app.applicationCode === productId)
      ?.products.map((product) => product.productCode) ?? []
  );
}

/**
 * The classes this product entitles — the list the class dropdown offers.
 *
 * Scoped to this user, so holding the product also proves the class is theirs.
 * That makes the org-wide class list redundant: a class the teacher has but
 * whose students hold nothing is not a class this report can open on.
 */
async function entitledClassIds(
  productId: string,
  /** Whose classes; the signed-in teacher's own unless an admin names an educator. */
  scope?: { orgId: string; userId: string }
) {
  const productCodes = await productCodesFor(productId);
  if (productCodes.length === 0) return new Set<string>();

  const { user } = await authLoader();
  const classes = await queryClient.ensureQueryData(
    classesByEdAppQuery({
      orgId: scope?.orgId ?? user.currentOrg.orgId,
      userId: scope?.userId ?? user.userId,
      edApp: productId,
      productCodes,
    })
  );
  return new Set(classes.map((cls) => cls.classId));
}

/** The students in one class entitled to this product. */
async function entitledStudentIds(productId: string, classId: string) {
  const productCodes = await productCodesFor(productId);
  if (productCodes.length === 0) return [] as string[];

  const students = await queryClient.ensureQueryData(
    studentsByClassAndEdAppQuery({ classId, edApp: productId, productCodes })
  );
  return students.map((student) => student.id);
}

/**
 * Every named student must hold the product, the way the class itself must.
 *
 * Belonging to the class is not enough: the student dropdown only offers the
 * entitled ones, so any other id is a link to a report that does not exist.
 * Answers with that roster, for callers with a rule of their own about it.
 */
async function validateEntitledStudents(
  args: LoaderFunctionArgs,
  classId: string,
  studentIds: string[]
) {
  const entitled = await entitledStudentIds(args.params.productId!, classId);
  if (!studentIds.every((id) => entitled.includes(id))) {
    // eslint-disable-next-line @typescript-eslint/no-throw-literal
    throw new Response('Not Found', { status: 404 });
  }
  return entitled;
}

/**
 * The classes a class-scoped report can open on.
 *
 * Nothing entitled means nothing to report on — the class dropdown would be
 * empty and every figure blank, so the report is not found rather than shown
 * hollow. Named classes must each hold the product; the endpoint is scoped to
 * this user, so holding it also proves the class is theirs.
 */
async function validateEntitledClasses(
  args: LoaderFunctionArgs,
  classIds: string[]
) {
  // The admin reports are scoped by organization and educator, never by the
  // reader's own roster — an admin has no classes of their own, and the
  // all-organizations report does not ask about any. The admin class
  // drill-down checks the educator's list instead, in its own guard.
  if (await usesAdminExperience()) {
    return;
  }

  const entitled = await entitledClassIds(args.params.productId!);
  if (entitled.size === 0 || !classIds.every((id) => entitled.has(id))) {
    // eslint-disable-next-line @typescript-eslint/no-throw-literal
    throw new Response('Not Found', { status: 404 });
  }
}

// Requires exactly 1 classId that exists in the user's classes. Returns the matched class.
async function validateSingleClass(
  reportPath: ReportPath,
  args: LoaderFunctionArgs
) {
  // Throws 404 if from/to date are invalid
  validateDateRangeParams(args.request);
  // Throws 404 if report not allowed for this product
  await reportGuardLoader(reportPath)(args);
  const classIds = new URL(args.request.url).searchParams.getAll('classId');
  if (classIds.length !== 1) {
    // eslint-disable-next-line @typescript-eslint/no-throw-literal
    throw new Response('Not Found', { status: 404 });
  }
  await validateEntitledClasses(args, classIds);

  return classIds[0]!;
}

// All-classes view: classIds are optional, but if provided they must all be valid.
function allClassesViewGuardLoader(reportPath: ReportPath) {
  return async (args: LoaderFunctionArgs) => {
    // Throws 404 if from/to date are invalid
    validateDateRangeParams(args.request);
    // Throws 404 if report not allowed for this product
    await reportGuardLoader(reportPath)(args);
    const classIds = new URL(args.request.url).searchParams.getAll('classId');
    await validateEntitledClasses(args, classIds);
    return null;
  };
}

// Engagement feed: classIds are optional; studentIds are optional but require exactly 1 classId
// and must belong to that class.
function engagementFeedGuardLoader(reportPath: ReportPath) {
  return async (args: LoaderFunctionArgs) => {
    // Throws 404 if from/to date are invalid
    validateDateRangeParams(args.request);
    await reportGuardLoader(reportPath)(args);
    const url = new URL(args.request.url);
    const classIds = url.searchParams.getAll('classId');
    const studentIds = url.searchParams.getAll('studentId');

    await validateEntitledClasses(args, classIds);

    if (classIds.length > 0) {
      // Students require exactly 1 class and must hold the product in it
      if (studentIds.length > 0) {
        if (classIds.length !== 1) {
          // eslint-disable-next-line @typescript-eslint/no-throw-literal
          throw new Response('Not Found', { status: 404 });
        }
        await validateEntitledStudents(args, classIds[0]!, studentIds);
      }
    } else if (studentIds.length > 0) {
      // studentIds without any classId is invalid
      // eslint-disable-next-line @typescript-eslint/no-throw-literal
      throw new Response('Not Found', { status: 404 });
    }

    return null;
  };
}

/**
 * The organization drill-down. Admin only, and the organization must be one the
 * admin can see — entitlement to the product is deliberately not the test.
 */
async function validateSingleKnownOrg(
  reportPath: ReportPath,
  args: LoaderFunctionArgs
) {
  if (!(await usesAdminExperience())) {
    // eslint-disable-next-line @typescript-eslint/no-throw-literal
    throw new Response('Not Found', { status: 404 });
  }

  validateDateRangeParams(args.request);
  await reportGuardLoader(reportPath)(args);

  const orgIds = new URL(args.request.url).searchParams.getAll('orgId');
  const [orgId] = orgIds;
  if (orgIds.length !== 1 || !orgId) {
    // eslint-disable-next-line @typescript-eslint/no-throw-literal
    throw new Response('Not Found', { status: 404 });
  }

  const orgs = await queryClient.ensureQueryData(orgHierarchyQuery());
  if (!orgs.some((org) => org.id === orgId)) {
    // eslint-disable-next-line @typescript-eslint/no-throw-literal
    throw new Response('Not Found', { status: 404 });
  }

  return orgId;
}

export function organizationViewGuardLoader(reportPath: ReportPath) {
  return async (args: LoaderFunctionArgs) => {
    await validateSingleKnownOrg(reportPath, args);
    return null;
  };
}

/**
 * One organization and one educator within it, which every admin level below
 * the organization shares. Answers with the pair it validated.
 */
async function validateSingleKnownTeacher(
  reportPath: ReportPath,
  args: LoaderFunctionArgs
) {
  const orgId = await validateSingleKnownOrg(reportPath, args);

  const teacherIds = new URL(args.request.url).searchParams.getAll('teacherId');
  const [teacherId] = teacherIds;
  if (teacherIds.length !== 1 || !teacherId) {
    // eslint-disable-next-line @typescript-eslint/no-throw-literal
    throw new Response('Not Found', { status: 404 });
  }

  // The educator has to be one of that organization's own, so a link cannot
  // be edited into a report on somebody else's staff.
  const teachers = await queryClient.ensureQueryData(teachersByOrgQuery(orgId));
  if (!teachers.some((teacher) => teacher.userId === teacherId)) {
    // eslint-disable-next-line @typescript-eslint/no-throw-literal
    throw new Response('Not Found', { status: 404 });
  }

  return { orgId, teacherId };
}

/** The educator drill-down: one organization and one educator within it. */
export function teacherViewGuardLoader(reportPath: ReportPath) {
  return async (args: LoaderFunctionArgs) => {
    await validateSingleKnownTeacher(reportPath, args);
    return null;
  };
}

/**
 * The admin class drill-down: the educator checks, and one class of theirs
 * that holds the product. The entitled list is read for the educator being
 * reported on, not for the admin reading it.
 */
async function validateSingleKnownClass(
  reportPath: ReportPath,
  args: LoaderFunctionArgs
) {
  const { orgId, teacherId } = await validateSingleKnownTeacher(
    reportPath,
    args
  );

  const classIds = new URL(args.request.url).searchParams.getAll('classId');
  const [classId] = classIds;
  if (classIds.length !== 1 || !classId) {
    // eslint-disable-next-line @typescript-eslint/no-throw-literal
    throw new Response('Not Found', { status: 404 });
  }

  const entitled = await entitledClassIds(args.params.productId!, {
    orgId,
    userId: teacherId,
  });
  if (!entitled.has(classId)) {
    // eslint-disable-next-line @typescript-eslint/no-throw-literal
    throw new Response('Not Found', { status: 404 });
  }

  return { orgId, teacherId, classId };
}

export function adminClassViewGuardLoader(reportPath: ReportPath) {
  return async (args: LoaderFunctionArgs) => {
    await validateSingleKnownClass(reportPath, args);
    return null;
  };
}

/**
 * The admin student drill-down: the class checks, and one student of that
 * class who holds the product. The roster is read for the class being
 * reported on, so an id from anywhere else is not found.
 */
export function adminStudentViewGuardLoader(reportPath: ReportPath) {
  return async (args: LoaderFunctionArgs) => {
    const { classId } = await validateSingleKnownClass(reportPath, args);

    const studentIds = new URL(args.request.url).searchParams.getAll(
      'studentId'
    );
    if (studentIds.length !== 1) {
      // eslint-disable-next-line @typescript-eslint/no-throw-literal
      throw new Response('Not Found', { status: 404 });
    }

    await validateEntitledStudents(args, classId, studentIds);
    return null;
  };
}

/** One organization is nothing to choose between, so the report opens on it. */
export async function soleOrganizationRedirect({
  request,
  params,
}: LoaderFunctionArgs) {
  if (!(await usesAdminExperience())) return null;

  const orgs = await queryClient.ensureQueryData(orgHierarchyQuery());
  const soleOrg = orgs.length === 1 ? orgs[0] : undefined;
  if (!soleOrg) return null;

  const url = new URL(request.url);
  const search = new URLSearchParams();
  const from = url.searchParams.get('from');
  const to = url.searchParams.get('to');
  if (from && to) {
    search.set('from', from);
    search.set('to', to);
  }
  search.set('orgId', soleOrg.id);

  return redirect(
    `/data/${params.productId}/overview/${
      Drilldown.Organization
    }?${search.toString()}`
  );
}

/** Teacher checks plus the admin orgId check; each runs only when it applies. */
export function sharedViewGuardLoader(reportPath: ReportPath) {
  return async (args: LoaderFunctionArgs) => {
    await validateOrgParams(args);

    const soleOrg = await soleOrganizationRedirect(args);
    if (soleOrg) return soleOrg;

    return allClassesViewGuardLoader(reportPath)(args);
  };
}

const SKILL_PERFORMANCE: ReportPath = 'skill-performance';

/**
 * The class view: exactly 1 class, which has to hold the product and to have
 * students in it holding the product — the view reports on those students, so
 * without any there is nothing to show. Named students must be among them.
 */
export function classViewGuardLoader(reportPath: ReportPath) {
  return async (args: LoaderFunctionArgs) => {
    const classId = await validateSingleClass(reportPath, args);
    const studentIds = new URL(args.request.url).searchParams.getAll(
      'studentId'
    );

    const entitled = await validateEntitledStudents(args, classId, studentIds);
    if (entitled.length === 0) {
      // eslint-disable-next-line @typescript-eslint/no-throw-literal
      throw new Response('Not Found', { status: 404 });
    }

    return null;
  };
}

/** `overview/class` serves both dashboards; each has its own rules. */
export function sharedClassViewGuardLoader(reportPath: ReportPath) {
  return async (args: LoaderFunctionArgs) => {
    if (await usesAdminExperience()) {
      return adminClassViewGuardLoader(reportPath)(args);
    }
    return classViewGuardLoader(reportPath)(args);
  };
}

// Student view: requires exactly 1 classId AND exactly 1 studentId that holds
// the product in that class.
export function studentViewGuardLoader(reportPath: ReportPath) {
  return async (args: LoaderFunctionArgs) => {
    const classId = await validateSingleClass(reportPath, args);
    const studentIds = new URL(args.request.url).searchParams.getAll(
      'studentId'
    );
    if (studentIds.length !== 1) {
      // eslint-disable-next-line @typescript-eslint/no-throw-literal
      throw new Response('Not Found', { status: 404 });
    }

    await validateEntitledStudents(args, classId, studentIds);
    return null;
  };
}

/** `overview/student` likewise. */
export function sharedStudentViewGuardLoader(reportPath: ReportPath) {
  return async (args: LoaderFunctionArgs) => {
    if (await usesAdminExperience()) {
      return adminStudentViewGuardLoader(reportPath)(args);
    }
    return studentViewGuardLoader(reportPath)(args);
  };
}

/**
 * Shared guard for data routes.
 * - 0 supported apps → allows through (product landing shows empty state)
 * - 1 supported app  → redirect straight to its overview report
 * - 2+ supported apps → returns (caller decides what to do next)
 */
async function ensureMultipleDataProducts() {
  const applications = await applicationsForRequest();
  const supported = await Promise.all(
    applications.map((app) => isSupportedProductForRequest(app.applicationCode))
  );
  const supportedApps = applications.filter((_, index) => supported[index]);
  if (supportedApps.length === 1) {
    const app = supportedApps[0]!;
    // eslint-disable-next-line @typescript-eslint/no-throw-literal
    throw redirect(`/data/${app.applicationCode}/overview`);
  }
}

/**
 * Loader for the /data section. Warms the permissions lookup so the admin
 * answer is settled before render, rather than flashing the teacher view.
 */
const dataLayoutLoader: LoaderFunction = async () => {
  const result = await roleGuardLoader(['teacher', 'administrator'])();

  // A Response means a disallowed role was redirected, and an administrator is
  // already settled by role alone — neither needs the permissions lookup.
  const needsPermissions =
    !(result instanceof Response) &&
    result.user.currentRole !== 'administrator';

  if (needsPermissions) {
    // A failure here is not fatal: the user is simply treated as a non-admin.
    await queryClient
      .ensureQueryData({
        queryKey: getPermissionsApiQueryKey(),
        queryFn: () => getPermissionsApi(),
      })
      .catch(() => undefined);
  }

  return result;
};

export const routes = createRoutesFromElements(
  <>
    <Route
      element={
        <QueryClientProvider client={queryClient}>
          <RootLayout />
        </QueryClientProvider>
      }
      errorElement={<ErrorBoundary />}
      loader={async ({ request }) => {
        const { searchParams } = new URL(request.url);
        const accessCode = searchParams.get(ACCESS_CODE_QUERY_PARAM);
        const app = searchParams.get(APP_QUERY_PARAM);
        const appState = encodeAppState(searchParams.get(STATE_QUERY_PARAM));

        if (accessCode) {
          sessionStorage.setItem(ACCESS_CODE_STORAGE_KEY, accessCode);
        }
        if (isRedeemDeepLinkRequested(searchParams)) {
          sessionStorage.setItem(REDEEM_STORAGE_KEY, 'true');
        }
        if (app) {
          sessionStorage.setItem(APP_STORAGE_KEY, app);
        }
        if (appState) {
          sessionStorage.setItem(APP_STATE_STORAGE_KEY, appState);
        }

        // Query params are consumed into sessionStorage above. The app-launch
        // vs. no-access decision is deferred to homeDeepLinkLoader (home is the
        // deep-link landing route) so an un-entitled `?app=` can surface the
        // no-access dialog instead of blindly redirecting into the launch.
        await queryClient.prefetchQuery(meQueryOptions());

        return null;
      }}
    >
      <Route
        element={
          <AppProvider>
            <AppLayout />
          </AppProvider>
        }
        loader={async ({ request }) => {
          // The one auth gate. Every route below it may assume a user, because
          // a redirect here short-circuits the navigation and discards their
          // loader errors. Any failure to obtain the user lands on sign-in.
          let user;
          try {
            user = (await queryClient.ensureQueryData(meQueryOptions()))
              .userContext;
          } catch (error) {
            if (!isUnauthenticated(error)) {
              console.error('Could not load the current user', error);
            }
          }
          if (!user) {
            const url = new URL(request.url);
            const returnUrl = `${url.pathname}${url.search}${window.location.hash}`;
            sessionStorage.setItem('returnUrl', returnUrl);
            return redirect(`/sign-in`);
          }
          const returnUrl = sessionStorage.getItem('returnUrl');
          if (returnUrl) {
            sessionStorage.removeItem('returnUrl');
            window.location.replace(returnUrl); // prevents future loaders from overriding returnUrl redirect
          }
          const knockJwt = await queryClient.ensureQueryData(
            customJwtQuery('KNOCK')
          );
          return { user, knockJwt };
        }}
      >
        <Route
          path="/sitemap"
          lazy={async () => {
            const { SiteMapPage } = await import(
              '@/pages/SiteMapPage/SiteMapPage'
            );

            return {
              element: <SiteMapPage />,
              handle: { title: 'Site Map' },
            };
          }}
        />
        <Route
          path="/knowledge-library"
          handle={{ siteSection: SITE_SECTION.productExperience }}
          loader={async () => {
            const user = await queryClient.ensureQueryData(meQueryOptions());
            if (user?.userContext.currentRole === 'student') {
              return redirect('/student');
            }
            return null;
          }}
        >
          <Route
            index
            lazy={async () => {
              const { KnowledgeLibraryLandingPage } = await import(
                'eds-product-experience-ux'
              );

              return {
                element: <KnowledgeLibraryLandingPage />,
                handle: {
                  title: 'Landing Page - Knowledge Library',
                  pageType: PAGE_TYPE.landingPage,
                },
              };
            }}
          />
          <Route
            path="topic/:bundleId"
            loader={async () => {
              const user = await queryClient.ensureQueryData(meQueryOptions());

              if (user?.userContext.currentRole === 'student') {
                return redirect('/student');
              }
              return user;
            }}
            lazy={async () => {
              const { EducatorTopicPageKL } = await import(
                'eds-product-experience-ux'
              );
              return {
                element: <EducatorTopicPageKL />,
                handle: {
                  pageType: PAGE_TYPE.bundle,
                },
              };
            }}
          />
          <Route
            path="book/:pcmId"
            loader={composeLoaders(authLoaderWithSessionContext, () => ({
              productCode: 'KL',
            }))}
            lazy={async () => {
              const { DefaultEducatorAssetPage } = await import(
                'eds-product-experience-ux'
              );
              return {
                element: <DefaultEducatorAssetPage />,
                handle: {
                  pageType: PAGE_TYPE.standaloneAsset,
                },
              };
            }}
          />
          <Route
            path="book/:assetFamilyId/resource/:pcmId"
            loader={async () => {
              const user = await queryClient.ensureQueryData(meQueryOptions());

              return {
                user,
                productCode: 'KL',
              };
            }}
            lazy={async () => {
              const { AssetFamilyPageKL } = await import(
                'eds-product-experience-ux'
              );

              return {
                element: <AssetFamilyPageKL />,
                handle: {
                  pageType: PAGE_TYPE.assetFamily,
                },
              };
            }}
          />
          <Route
            path="pages/:sectionId"
            loader={async () => {
              const user = await queryClient.ensureQueryData(meQueryOptions());
              if (user?.userContext.currentRole === 'student') {
                return redirect('/student');
              }
              return null;
            }}
            lazy={async () => {
              const { KnowledgeLibraryListPage } = await import(
                'eds-product-experience-ux'
              );
              return {
                element: <KnowledgeLibraryListPage />,
              };
            }}
          />
          <Route
            path="pages/:sectionId/part/:subsectionId"
            loader={async () => {
              const user = await queryClient.ensureQueryData(meQueryOptions());
              if (user?.userContext.currentRole === 'student') {
                return redirect('/student');
              }
              return null;
            }}
            lazy={async () => {
              const { KnowledgeLibraryListPage } = await import(
                'eds-product-experience-ux'
              );
              return {
                element: <KnowledgeLibraryListPage />,
              };
            }}
          />
        </Route>
        <Route
          path="/explore-ela"
          handle={{ siteSection: SITE_SECTION.productExperience }}
        >
          {/* Product Routes */}
          <Route
            index
            lazy={async () => {
              const { EducatorProductPage, StudentProductPage } = await import(
                'eds-product-experience-ux'
              );
              const user = await queryClient.ensureQueryData(meQueryOptions());
              if (user?.userContext.currentRole === 'student') {
                return {
                  element: <StudentProductPage />,
                  handle: {
                    title: 'Explore ELA Landing Page',
                    pageType: PAGE_TYPE.landingPage,
                  },
                };
              }
              return {
                element: <EducatorProductPage />,
                handle: {
                  title: 'Explore ELA Landing Page',
                  pageType: PAGE_TYPE.landingPage,
                },
              };
            }}
          />
          <Route
            path="resource/:assetId/*"
            loader={async ({ request }) => {
              const user = await queryClient.ensureQueryData(meQueryOptions());

              // Check access for student work routes
              const workRouteRedirect = await checkAccessForStudentWorkRoutes(
                request
              );
              if (workRouteRedirect) return workRouteRedirect;

              if (user?.userContext.currentRole === 'student') {
                const rosterUser = user
                  ? await queryClient.ensureQueryData(
                      rosterUserQuery(user.userContext.userId)
                    )
                  : undefined;
                const grade = rosterUser?.grade;
                return {
                  user,
                  grade,
                };
              }

              return {
                user,
              };
            }}
            lazy={async () => {
              const { TeacherSingleAssetPage, StudentSingleAssetPage } =
                await import('eds-product-experience-ux');
              const user = await queryClient.ensureQueryData(meQueryOptions());
              if (user?.userContext.currentRole !== 'student') {
                return {
                  element: <TeacherSingleAssetPage />,
                  handle: {
                    title: storyPageTitle,
                    pageType: PAGE_TYPE.standaloneAsset,
                  },
                };
              }
              return {
                element: <StudentSingleAssetPage />,
                handle: {
                  title: 'Student Single Story Page',
                  pageType: PAGE_TYPE.standaloneAsset,
                },
              };
            }}
          />

          <Route
            path="unit/:unitId"
            loader={async () => {
              const user = await queryClient.ensureQueryData(meQueryOptions());

              if (user?.userContext.currentRole === 'student') {
                return redirect('/student');
              }
              return user;
            }}
            lazy={async () => {
              const { UnitCollectionPage } = await import(
                'eds-product-experience-ux'
              );
              return {
                element: <UnitCollectionPage />,
                handle: {
                  title: 'Unit Page',
                  pageType: PAGE_TYPE.bundle,
                },
              };
            }}
          />
          <Route
            path="page/:sectionId"
            loader={async () => {
              const user = await queryClient.ensureQueryData(meQueryOptions());
              if (user?.userContext.currentRole === 'student') {
                return redirect('/student');
              }
              return null;
            }}
            lazy={async () => {
              const { PackageSectionPage } = await import(
                'eds-product-experience-ux'
              );
              return {
                element: <PackageSectionPage />,
                handle: {
                  title: 'Library - Explore ELA',
                  pageType: PAGE_TYPE.packageSection,
                },
              };
            }}
          />
          <Route
            path="page/:sectionId/part/:subsectionId"
            loader={async () => {
              const user = await queryClient.ensureQueryData(meQueryOptions());
              if (user?.userContext.currentRole === 'student') {
                return redirect('/student');
              }
              return null;
            }}
            lazy={async () => {
              const { PackageSectionSubsectionPage } = await import(
                'eds-product-experience-ux'
              );
              return {
                element: <PackageSectionSubsectionPage />,
                handle: {
                  title: 'Library - Explore ELA',
                  pageType: PAGE_TYPE.packageSection,
                },
              };
            }}
          />
          <Route
            path="assets/:id/*"
            loader={async ({ request }) => {
              const user = await queryClient.ensureQueryData(meQueryOptions());

              // Check access for student work routes
              const workRouteRedirect = await checkAccessForStudentWorkRoutes(
                request
              );
              if (workRouteRedirect) return workRouteRedirect;

              if (user.userContext.currentRole === 'student') {
                const rosterUser = user
                  ? await queryClient.ensureQueryData(
                      rosterUserQuery(user.userContext.userId)
                    )
                  : undefined;

                const grade = rosterUser?.grade;

                return {
                  user,
                  grade,
                };
              }

              return { user };
            }}
            lazy={async () => {
              const { TeacherStoryPage, StudentStoryPage } = await import(
                'eds-product-experience-ux'
              );
              const user = await queryClient.ensureQueryData(meQueryOptions());
              if (user?.userContext.currentRole !== 'student') {
                return {
                  element: <TeacherStoryPage />,
                  handle: {
                    title: storyPageTitle,
                    pageType: PAGE_TYPE.assetFamily,
                  },
                };
              }
              return {
                element: <StudentStoryPage />,
                handle: {
                  title: storyPageTitle,
                  pageType: PAGE_TYPE.assetFamily,
                },
              };
            }}
          />

          <Route
            path="recent"
            loader={async () => {
              const user = await queryClient.ensureQueryData(meQueryOptions());

              if (user?.userContext.currentRole !== 'student') {
                return redirect('/');
              }
              return user;
            }}
            lazy={async () => {
              const { StudentHistoryPage } = await import(
                'eds-product-experience-ux'
              );
              return {
                element: <StudentHistoryPage />,
                handle: {
                  title: 'Student History',
                  pageType: PAGE_TYPE.studentHistory,
                },
              };
            }}
          />
        </Route>
        {/* Teacher Protected Routes */}
        <Route loader={roleGuardLoader(['teacher', 'administrator'])}>
          <Route
            id="home"
            path="/"
            loader={homeDeepLinkLoader}
            lazy={async () => {
              const { HomePage } = await import('@/pages/HomePage/HomePage');
              return {
                element: <HomePage />,
              };
            }}
            handle={{ title: 'Home' }}
          />
          <Route
            path="/classes"
            loader={async () => {
              const { userContext: user } = await queryClient.ensureQueryData(
                meQueryOptions()
              );

              if (user.userSource === 'MyScholastic') {
                return redirect('/');
              }

              return user;
            }}
            lazy={async () => {
              const { RosterClassesPage } = await import('eds-roster-ux');
              return {
                element: <RosterClassesPage />,
                handle: {
                  title: 'Classes',
                  siteSection: SITE_SECTION.classes,
                },
              };
            }}
          />
          <Route
            path="/classes/details/:classId"
            lazy={async () => {
              const { RosterClassDetailsPage } = await import('eds-roster-ux');
              return {
                element: <RosterClassDetailsPage />,
                handle: { title: 'Class Details' },
              };
            }}
          />
          <Route
            path="/classes/details/:classId/student/:studentId"
            lazy={async () => {
              const { RosterStudentDetailsPage } = await import(
                'eds-roster-ux'
              );
              return {
                element: <RosterStudentDetailsPage />,
                handle: { title: 'Student Details' },
              };
            }}
          />

          <Route
            path="/subscriptions"
            lazy={async () => {
              const { SubscriptionsPage } = await import(
                '@/pages/SubscriptionsPage/SubscriptionsPage'
              );
              return {
                element: <SubscriptionsPage />,
                handle: {
                  title: 'Subscriptions',
                  siteSection: SITE_SECTION.subscriptions,
                },
              };
            }}
          />
        </Route>

        {/* Data Routes — teacher + admin (admin sees Coming Soon via DataRoleGate) */}
        <Route
          id="data-layout"
          loader={dataLayoutLoader}
          errorElement={<DataErrorBoundary />}
          shouldRevalidate={({ currentUrl, nextUrl }) =>
            currentUrl.pathname !== nextUrl.pathname
          }
        >
          {/* /data → redirect to product landing (or directly to overview if single app) */}
          <Route
            path="/data"
            loader={async () => {
              await ensureMultipleDataProducts();
              return redirect('/data/product-landing');
            }}
          />

          {/* /data/product-landing → product selection (skip if single app) */}
          <Route
            path="/data/product-landing"
            element={
              <ByRole
                admin={<AdminProductLandingPage />}
                teacher={<ProductLandingPage />}
              />
            }
            handle={{ title: 'Data', siteSection: SITE_SECTION.data }}
            loader={async () => {
              await ensureMultipleDataProducts();
              return null;
            }}
          />

          {/* /data/:productId → report layout shell + all report routes */}
          <Route
            path="/data/:productId"
            element={
              <ByRole admin={<AdminDataSection />} teacher={<ReportLayout />} />
            }
            handle={{ siteSection: SITE_SECTION.data }}
            loader={async ({ params }) => {
              const productId = params.productId!;

              // Check 1: Is this app in the dashboard config for this user?
              if (!(await isSupportedProductForRequest(productId))) {
                // eslint-disable-next-line @typescript-eslint/no-throw-literal
                throw new Response('Not Found', { status: 404 });
              }

              // Check 2: Does the user have access from the fulfillment API?
              const applications = await applicationsForRequest();
              const hasAccess = applications.some(
                (app) => app.applicationCode === productId
              );
              if (!hasAccess) {
                // eslint-disable-next-line @typescript-eslint/no-throw-literal
                throw new Response('Not Found', { status: 404 });
              }

              return null;
            }}
          >
            {/* Default: redirect to overview */}
            <Route index loader={() => redirect('overview')} />

            {/* Overview report */}
            <Route
              path="overview"
              loader={sharedViewGuardLoader('overview')}
              element={
                <ByRole
                  admin={<AdminOverviewPage />}
                  teacher={<OverviewPage />}
                />
              }
              handle={{ title: 'Overview' }}
            />

            <Route
              path="overview/org"
              loader={organizationViewGuardLoader('overview')}
              element={
                <ByRole
                  admin={<AdminOverviewPage />}
                  teacher={<DataNotFoundPage />}
                />
              }
              handle={{ title: 'Organization Overview' }}
            />
            {/* Literal, as above. */}
            <Route
              path="overview/teacher"
              loader={teacherViewGuardLoader('overview')}
              element={
                <ByRole
                  admin={<AdminOverviewPage />}
                  teacher={<DataNotFoundPage />}
                />
              }
              handle={{ title: 'Teacher Overview' }}
            />
            <Route
              path="overview/class"
              loader={sharedClassViewGuardLoader('overview')}
              element={
                <ByRole
                  admin={<AdminOverviewPage />}
                  teacher={<OverviewPage />}
                />
              }
              handle={{ title: 'Class Overview' }}
            />
            <Route
              path="overview/student"
              loader={sharedStudentViewGuardLoader('overview')}
              element={
                <ByRole
                  admin={<AdminOverviewPage />}
                  teacher={<OverviewPage />}
                />
              }
              handle={{ title: 'Student Overview' }}
            />

            {/* Engagement report */}
            <Route
              path="engagement"
              loader={engagementFeedGuardLoader('engagement')}
              element={<EngagementFeedPage />}
              handle={{ title: 'Engagement Feed' }}
            />

            {/* Lexile report */}
            <Route
              path="lexile-measure"
              loader={allClassesViewGuardLoader('lexile-measure')}
              element={<LexilePage />}
              handle={{ title: 'Lexile' }}
            />
            <Route
              path="lexile-measure/class"
              loader={classViewGuardLoader('lexile-measure')}
              element={<LexilePage />}
              handle={{ title: 'Lexile — Class' }}
            />

            {/* GE report */}
            <Route
              path="ge-score"
              loader={allClassesViewGuardLoader('ge-score')}
              element={<GEPage />}
              handle={{ title: 'GE' }}
            />
            <Route
              path="ge-score/class"
              loader={classViewGuardLoader('ge-score')}
              element={<GEPage />}
              handle={{ title: 'GE — Class' }}
            />

            {/* Skill Performance report */}
            <Route
              path="skill-performance"
              loader={allClassesViewGuardLoader(SKILL_PERFORMANCE)}
              element={<SkillPage />}
              handle={{ title: 'Skill Performance' }}
            />
            <Route
              path="skill-performance/class"
              loader={classViewGuardLoader(SKILL_PERFORMANCE)}
              element={<SkillPage />}
              handle={{ title: 'Skill Performance — Class' }}
            />
            <Route
              path="skill-performance/student"
              loader={studentViewGuardLoader(SKILL_PERFORMANCE)}
              element={<SkillPage />}
              handle={{ title: 'Skill Performance — Student' }}
            />
          </Route>

          {/* Catch-all: any unmatched /data/* path → 404 */}
          <Route
            path="/data/*"
            loader={() => {
              // eslint-disable-next-line @typescript-eslint/no-throw-literal
              throw new Response('Not Found', { status: 404 });
            }}
          />
        </Route>

        {/* Site Auth Protected Routes */}
        <Route loader={roleGuardLoader(['site_auth', 'site_auth_admin'])}>
          <Route
            path="/siteauth"
            lazy={async () => {
              const { SiteAuthHomePage } = await import(
                '@/pages/HomePage/SiteAuthHomePage'
              );
              return {
                element: <SiteAuthHomePage />,
              };
            }}
            handle={{ title: 'SiteAuth Home' }}
          />
        </Route>

        {/* Student Protected Routes */}
        <Route
          path="/student"
          loader={roleGuardLoader(['student', 'classPassword'])}
        >
          <Route
            index
            loader={studentHomeDeepLinkLoader}
            lazy={async () => {
              const { StudentHomePage } = await import(
                '@/pages/HomePage/StudentHomePage'
              );
              return {
                element: <StudentHomePage />,
              };
            }}
            handle={{ title: 'Student Home' }}
          />
        </Route>

        {/* Admin Protected Routes */}
        <Route
          path="/users"
          handle={{ siteSection: SITE_SECTION.users }}
          loader={async () => {
            const permissions = await queryClient.ensureQueryData({
              queryKey: getPermissionsApiQueryKey(),
              queryFn: () => getPermissionsApi(),
            });

            if (!permissions?.manageAllUsers) {
              return redirect('/');
            }
            return null;
          }}
        >
          <Route
            index
            lazy={async () => {
              const { UsersLandingPage } = await import('eds-users-ux');

              return {
                handle: { title: 'Users' },
                element: <UsersLandingPage />,
              };
            }}
          />

          <Route
            path="/users/details/:userId"
            lazy={async () => {
              const { UserDetailsPage } = await import('eds-users-ux');

              return {
                handle: { title: 'User Details' },
                element: <UserDetailsPage />,
              };
            }}
          />
        </Route>

        <Route
          path="/omni-reader"
          lazy={async () => {
            const { OmniReaderPage } = await import(
              '@/pages/OmniReaderPage/OmniReaderPage'
            );
            return {
              element: <OmniReaderPage />,
              handle: {
                title: 'OmniReader',
              },
              loader: async () => {
                const user = (
                  await queryClient.ensureQueryData(meQueryOptions())
                ).userContext;
                return queryClient.ensureQueryData(
                  rosterUserQuery(user.userId)
                );
              },
            };
          }}
        />
        <Route
          path="/search"
          lazy={async () => {
            const { SearchPage } = await import(
              '@/pages/SearchPage/SearchPage'
            );
            return {
              element: <SearchPage />,
              handle: {
                title: 'Search Results',
              },
            };
          }}
        />
      </Route>

      <Route path="/login" loader={() => redirect('/sign-in')} />
      <Route path="/role" loader={() => redirect('/sign-in')} />
      <Route
        id="sign-in"
        path="/sign-in"
        loader={async () => queryClient.ensureQueryData(clientEnvsQuery())}
      >
        <Route
          index
          lazy={async () => {
            const { SignInPage } = await import('@authentication/index');
            return {
              element: (
                <SignInPage
                  onSignIn={(res) => {
                    const parsed = meResponseShape.safeParse(res);
                    if (parsed.success && parsed.data.sessionContext) {
                      queryClient.setQueryData(
                        meQueryOptions().queryKey,
                        parsed.data
                      );
                    } else {
                      queryClient.removeQueries({
                        queryKey: meQueryOptions().queryKey,
                      });
                    }
                  }}
                />
              ),
              handle: {
                title: 'Sign In',
              },
            };
          }}
        />
        <Route
          path="class-password"
          lazy={async () => {
            const { ClassroomPasswordPage } = await import(
              '@authentication/index'
            );
            return {
              element: (
                <ClassroomPasswordPage
                  onSignIn={() => {
                    queryClient.removeQueries({
                      queryKey: meQueryOptions().queryKey,
                    });
                  }}
                />
              ),
              handle: {
                title: 'Classroom Password Sign In',
              },
            };
          }}
        />
      </Route>
      <Route
        path="/google-onboard"
        lazy={async () => {
          const { GoogleOnboardLayout } = await import('@authentication/index');
          return {
            element: (
              <GoogleOnboardLayout>
                <Outlet />
              </GoogleOnboardLayout>
            ),
          };
        }}
      >
        <Route
          index
          lazy={async () => {
            const { GoogleOnboardStartPage } = await import(
              '@authentication/index'
            );
            return {
              element: <GoogleOnboardStartPage />,
              handle: {
                title: 'Google Classroom Onboarding',
              },
            };
          }}
        />
        <Route
          path="org-select"
          lazy={async () => {
            const { GoogleOnboardOrgSelectPage } = await import(
              '@authentication/index'
            );
            return {
              element: (
                <GoogleOnboardOrgSelectPage
                  onSignIn={() => {
                    queryClient.removeQueries({
                      queryKey: meQueryOptions().queryKey,
                    });
                  }}
                />
              ),
              handle: {
                title: 'Google Classroom - Select Organization',
              },
            };
          }}
        />
      </Route>
      <Route
        path="/sign-out"
        action={() => signOutAction(queryClient)}
        element={<SignOutPage />}
        handle={{ title: 'Sign Out' }}
        errorElement={<h1>Failed to sign out!</h1>}
      />
    </Route>

    <Route
      path="/*"
      element={
        <>
          <h1>Not Found</h1>
          <p>This page doesn&apos;t exist.</p>
        </>
      }
    />
  </>
);
